import { format } from "date-fns";
import { formatDate as formatDisplayDate } from '@/lib/formatDate';
import { CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Calendar, X } from "lucide-react";
import { cn } from "@/lib/utils";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TemplateMenu, TemplateOption, useApplyTemplate, useTemplateMatches, type RxTemplate } from "@/components/Template/RxTemplates";
import { useClinicalTerms } from "@/lib/clinicalTerms";
import TermSuggestion from "./TermSuggestion";

// ─── Options ─────────────────────────────────────────────────────────────────

// The complaint list itself (names, what doctors type for them, who they fit) comes from lib/clinicalTerms

const FREQUENCY_OPTIONS = [
  "Continuous", "Multiple times daily", "3-4 times daily",
  "Twice daily", "Once daily", "Occasionally",
  "At night only", "In morning only", "After meals", "Before meals",
  "On exertion",
];

const SEVERITY_OPTIONS = ["Mild", "Moderate", "Severe", "Very severe"];

const DURATION_OPTIONS = [
  "Since today", "Since yesterday", "Since last night",
  "1 day", "2 days", "3 days",
  "1 week", "2 weeks",
  "1 month", "2 months", "3 months",
  "Chronic",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const calculateDateFromDuration = (duration: string): string => {
  const today = new Date();
  const dl = duration.toLowerCase().trim();
  if (dl.includes("today") || dl.includes("morning"))
    return format(today, "yyyy-MM-dd");
  if (dl.includes("yesterday") || dl.includes("last night")) {
    const d = new Date(today); d.setDate(d.getDate() - 1);
    return format(d, "yyyy-MM-dd");
  }
  const dayMatch = /(\d+)\s*day/.exec(dl);
  if (dayMatch) { const d = new Date(today); d.setDate(d.getDate() - parseInt(dayMatch[1])); return format(d, "yyyy-MM-dd"); }
  const weekMatch = /(\d+)\s*week/.exec(dl);
  if (weekMatch) { const d = new Date(today); d.setDate(d.getDate() - parseInt(weekMatch[1]) * 7); return format(d, "yyyy-MM-dd"); }
  const monthMatch = /(\d+)\s*month/.exec(dl);
  if (monthMatch) { const d = new Date(today); d.setMonth(d.getMonth() - parseInt(monthMatch[1])); return format(d, "yyyy-MM-dd"); }
  return "";
};

const formatDateDisplay = (dateStr: string): string => {
  return formatDisplayDate(dateStr);
};

// showPicker() isn't supported on older Safari/iPadOS — fall back to focus,
// which still opens the native picker on those browsers.
const openDatePicker = (input: HTMLInputElement | null) => {
  if (!input) return;
  if (typeof input.showPicker === "function") input.showPicker();
  else input.focus();
};

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_ROW = { complaint: "", frequency: "", severity: "", duration: "", date: "" };

// "Fever 3 days", "cough 2w", "headache since yesterday" → the complaint and how long
const DURATION_TAIL = /\s+(since\s+(?:today|yesterday|last night|morning)|today|yesterday|last night|chronic|\d+\s*(?:d|days?|w|wks?|weeks?|m|mo|months?|y|yrs?|years?))$/i;

const normaliseDuration = (raw: string): string => {
  const r = raw.trim().toLowerCase();
  const m = /^(\d+)\s*(d|days?|w|wks?|weeks?|m|mo|months?|y|yrs?|years?)$/.exec(r);
  if (m) {
    const n = Number(m[1]);
    const unit = { d: "day", w: "week", m: "month", y: "year" }[m[2][0] as "d" | "w" | "m" | "y"];
    return `${n} ${unit}${n === 1 ? "" : "s"}`;
  }
  if (r.startsWith("since")) return `Since ${r.slice(5).trim()}`;
  if (["today", "yesterday", "last night"].includes(r)) return `Since ${r}`;
  if (r === "chronic") return "Chronic";
  return raw.trim();
};

export const splitComplaint = (text: string): { complaint: string; duration: string } => {
  const m = DURATION_TAIL.exec(text);
  if (!m || m.index === 0) return { complaint: text.trim(), duration: "" };
  return { complaint: text.slice(0, m.index).trim(), duration: normaliseDuration(m[1]) };
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Complaints as chips in one box, as in the mockup ("Fever · 3 days"). Typing "Fever 3 days" or picking
 * from the list adds one; a chip opens its details (frequency, severity, duration, since).
 */
export default function ComplaintsGrid({ specialty, patientAgeMonths, patientSex }: { specialty?: string; patientAgeMonths?: number | null; patientSex?: string | null }) {
  const { control, setValue, getValues, watch } = useFormContext();
  const { fields, append, remove, update } = useFieldArray({ control, name: "complaints" });
  const watched: any[] = watch("complaints") || [];

  const [text, setText]                 = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [openId, setOpenId]             = useState<string | null>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dateRef      = useRef<HTMLInputElement>(null);

  const chips = fields
    .map((f, index) => ({ id: f.id, index, c: watched[index] ?? {} }))
    .filter(({ c }) => String(c.complaint ?? "").trim());

  const typed = splitComplaint(text).complaint.toLowerCase();
  const taken = new Set(chips.map(({ c }) => String(c.complaint).toLowerCase()));
  // Empty box: this doctor's most-used complaints, then the usual ones for this patient; typing searches the
  // whole list, aliases included ("bukhar" → Fever). Complaints that can't fit the patient are left out.
  const complaintTerms = useClinicalTerms("complaint");
  const matches = complaintTerms.suggest(typed, { sex: patientSex, ageMonths: patientAgeMonths }, specialty, taken);
  const suggestions = matches.map((m) => m.term.name);

  // Enter takes the top suggestion only when what's typed is the start of its name and not a word in its own
  // right: "fev" → Fever, but "pain" stays "pain", never "Painful Periods". The highlight shows which it will be;
  // the arrows reach the other suggestions and "Add …".
  const prefixPick = typed.length >= 2 && !!matches[0]?.term.name.toLowerCase().startsWith(typed)
    && !complaintTerms.find(typed) && !complaintTerms.isWord(typed);
  useEffect(() => { setFocusedIndex(prefixPick ? 0 : -1); }, [typed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Saved templates whose name is being typed — listed after the complaints, reached with the arrows
  const templates = useTemplateMatches("complaint", text);
  const showAdd = !!text.trim() && !suggestions.some((o) => o.toLowerCase() === typed) && !complaintTerms.find(typed);
  const applyTemplate = useApplyTemplate();
  const pickTemplate = (template: RxTemplate) => {
    applyTemplate?.(template);
    setText("");
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const setDuration = (index: number, duration: string) => {
    setValue(`complaints.${index}.duration`, duration, { shouldDirty: true });
    setValue(`complaints.${index}.date`, duration.trim() ? calculateDateFromDuration(duration) : "", { shouldDirty: true });
  };

  const addComplaint = (complaint: string, duration: string) => {
    if (!complaint.trim()) return;
    const rows = getValues("complaints") ?? [];
    let index = rows.findIndex((c: any) => !String(c?.complaint ?? "").trim());
    if (index === -1) { append(DEFAULT_ROW, { shouldFocus: false }); index = rows.length; }
    setValue(`complaints.${index}.complaint`, complaint.trim(), { shouldDirty: true });
    if (duration) setDuration(index, duration);
    setText("");
    setTimeout(() => { inputRef.current?.focus(); setShowSuggestions(true); }, 0);
  };

  const commit = (picked?: string) => {
    const { complaint, duration } = splitComplaint(text);
    // Typed a listed complaint's name or alias: use the list's name ("fever" / "bukhar" → "Fever")
    const listed = complaintTerms.find(complaint)?.name;
    const typedName = complaint.charAt(0).toUpperCase() + complaint.slice(1);
    addComplaint(picked ?? listed ?? typedName, duration);
  };

  const removeComplaint = (index: number) => {
    setOpenId(null);
    if (fields.length > 1) remove(index);
    else update(index, { ...DEFAULT_ROW });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // The "Add …" row comes last
    const addIndex = showAdd ? suggestions.length + templates.length : -1;
    const max = suggestions.length + templates.length - (showAdd ? 0 : 1);
    switch (e.key) {
      case "ArrowDown": e.preventDefault(); setShowSuggestions(true); setFocusedIndex((p) => (p < max ? p + 1 : 0)); break;
      case "ArrowUp":   e.preventDefault(); setFocusedIndex((p) => (p > 0 ? p - 1 : max)); break;
      case "Enter":
      case ",":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) commit(suggestions[focusedIndex]);
        else if (focusedIndex >= suggestions.length && focusedIndex < suggestions.length + templates.length) pickTemplate(templates[focusedIndex - suggestions.length]);
        else if (focusedIndex === addIndex) commit();
        else if (text.trim()) commit();
        break;
      case "Escape": setShowSuggestions(false); break;
      case "Tab":    setShowSuggestions(false); break;
    }
  };

  const getDropdownStyle = (): CSSProperties => {
    if (!inputRef.current) return {};
    const rect = inputRef.current.getBoundingClientRect();
    const wouldOverflowBottom = rect.bottom + 296 > window.innerHeight - 20;
    return {
      position: "fixed", minWidth: 260, zIndex: 9999, left: rect.left,
      ...(wouldOverflowBottom ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    };
  };

  return (
    <div ref={containerRef}>
      <div
        className={cn(
          "min-h-9 touch:min-h-10 px-1.5 py-[3px] flex flex-wrap items-center gap-1.5 rounded-md border border-field bg-card cursor-text transition-colors",
          "hover:border-ring/60 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20",
        )}
        onClick={(e) => { if (e.target === e.currentTarget) inputRef.current?.focus(); }}
      >
        {chips.map(({ id, index, c }) => {
          const details = [c.duration, c.severity, c.frequency].filter(Boolean).join(" · ");
          // Opening a chip's details closes the add box's list (left open after adding a complaint)
          return (
            <Popover key={id} open={openId === id} onOpenChange={(o) => { setOpenId(o ? id : null); if (o) setShowSuggestions(false); }}>
              <span className="inline-flex items-center h-[26px] touch:h-8 rounded bg-muted text-[13px] text-foreground">
                <PopoverTrigger asChild>
                  <button type="button" className="h-full pl-2 pr-1 flex items-center gap-1.5 rounded-l hover:bg-accent" title={details ? `${c.complaint} · ${details}` : `${c.complaint} — add details`}>
                    <span>{c.complaint}</span>
                    {c.duration && <span className="text-muted-foreground">{c.duration}</span>}
                  </button>
                </PopoverTrigger>
                <button
                  type="button"
                  onClick={() => removeComplaint(index)}
                  aria-label={`Remove ${c.complaint}`}
                  className="touch-target h-full px-1.5 rounded-r text-muted-foreground hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
              <PopoverContent align="start" className="w-72 p-3 space-y-2.5">
                <p className="text-sm font-semibold text-foreground">{c.complaint}</p>
                {([
                  ["Duration", "duration", DURATION_OPTIONS],
                  ["Severity", "severity", SEVERITY_OPTIONS],
                  ["Frequency", "frequency", FREQUENCY_OPTIONS],
                ] as const).map(([label, key, options]) => (
                  <div key={key} className="grid grid-cols-[76px_minmax(0,1fr)] items-center gap-2">
                    <span className="text-[13px] text-muted-foreground">{label}</span>
                    <AutocompleteInput
                      value={c[key] || ""}
                      onChange={(val) => key === "duration" ? setDuration(index, val) : setValue(`complaints.${index}.${key}`, val, { shouldDirty: true })}
                      options={[...options]}
                      showAllOnFocus={true}
                      dropdownMinWidth={160}
                      variant="outlined"
                      inputSize="md"
                      containerClassName="h-9 flex items-center"
                    />
                  </div>
                ))}
                <div className="grid grid-cols-[76px_minmax(0,1fr)] items-center gap-2">
                  <span className="text-[13px] text-muted-foreground">Since</span>
                  <button
                    type="button"
                    onClick={() => openDatePicker(dateRef.current)}
                    className="relative h-9 px-2 flex items-center gap-1.5 rounded-md border border-field bg-card text-sm text-left"
                  >
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                    {c.date ? formatDateDisplay(c.date) : <span className="text-muted-foreground/60">Select date</span>}
                    <input
                      ref={dateRef}
                      type="date"
                      value={c.date || ""}
                      onChange={(e) => setValue(`complaints.${index}.date`, e.target.value, { shouldDirty: true })}
                      className="absolute bottom-0 left-0 opacity-0 w-px h-px pointer-events-none"
                      tabIndex={-1}
                    />
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={chips.length ? "Add another…" : "Add complaint…"}
          aria-label="Add complaint"
          className="flex-1 min-w-[160px] h-7 touch:h-9 px-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground/60"
        />
        <TemplateMenu type="complaint" />
      </div>

      {showSuggestions && (suggestions.length > 0 || text.trim()) && createPortal(
        <div className="bg-popover border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto" style={getDropdownStyle()}>
          {matches.map((m, i) => (
            <TermSuggestion
              key={m.term.id}
              match={m}
              active={i === focusedIndex}
              onPick={() => commit(m.term.name)}
              onForget={() => complaintTerms.forget(m.term.id)}
              extra={splitComplaint(text).duration}
            />
          ))}
          {templates.map((t, i) => (
            <TemplateOption key={t.id} template={t} active={suggestions.length + i === focusedIndex} onPick={() => pickTemplate(t)} />
          ))}
          {showAdd && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(); }}
              className={cn(
                "w-full text-left px-3 py-2 text-sm border-t border-border/40 text-muted-foreground hover:bg-accent",
                focusedIndex === suggestions.length + templates.length && "bg-accent text-foreground",
              )}
            >
              Add “{text.trim()}”
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
